// Data layer for the raw record pages (/api/records). Relative URLs so it works on any host.
export const fetchRecords = async () => {
  const response = await fetch('/api/records', { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
};

// Resolves to null when the record does not exist
export const fetchRecord = async (id) => {
  const response = await fetch(`/api/records/${encodeURIComponent(id)}`, {
    headers: { Accept: 'application/json' }
  });
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
};

export const RECORD_FIELDS = [
  { key: 'Address__c', label: 'Address', type: 'text' },
  { key: 'Asking_Price__c', label: 'Asking Price', type: 'text' },
  { key: 'Price__c', label: 'Price', type: 'text' },
  { key: 'Beds__c', label: 'Beds', type: 'text' },
  { key: 'Baths__c', label: 'Baths', type: 'text' },
  { key: 'BER_Rating__c', label: 'BER Rating', type: 'text' },
  { key: 'Broker__c', label: 'Broker', type: 'text' },
  { key: 'DaftUrl__c', label: 'Daft URL', type: 'text' },
  { key: 'Date_Listed__c', label: 'Date Listed', type: 'text' },
  { key: 'Location__Latitude__s', label: 'Latitude', type: 'number' },
  { key: 'Location__Longitude__s', label: 'Longitude', type: 'number' },
  { key: 'sqm__c', label: 'Floor Area (m²)', type: 'text' },
  { key: 'Status__c', label: 'Status', type: 'text' },
  { key: 'Eircode', label: 'Eircode', type: 'text' }
];

// Sends only the changed fields. Throws an Error carrying the server's message on failure.
export const updateRecord = async (id, patch) => {
  const response = await fetch(`/api/records/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(patch)
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `HTTP error! status: ${response.status}`);
  return body;
};

export const deleteRecord = async (id) => {
  const response = await fetch(`/api/records/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json' }
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `HTTP error! status: ${response.status}`);
};

// Creates a listing; the server generates a Daft ID if none is given. Returns the saved record.
export const createRecord = async (values) => {
  const response = await fetch('/api/records', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(values)
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `HTTP error! status: ${response.status}`);
  return body;
};

// Stores a JPG as daft-images/<id>.jpg on the server (replaces any existing image)
export const uploadImage = async (id, file) => {
  const form = new FormData();
  form.append('file', file);
  const response = await fetch(`/api/images/${encodeURIComponent(id)}`, { method: 'POST', body: form });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `HTTP error! status: ${response.status}`);
  return body;
};

// ---- CSV import ----
const postFile = async (url, fields) => {
  const form = new FormData();
  Object.entries(fields).forEach(([key, value]) => form.append(key, value));
  const response = await fetch(url, { method: 'POST', body: form });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `HTTP error! status: ${response.status}`);
  return body;
};

export const previewImport = (file) => postFile('/api/import/preview', { file });

const postJson = async (url, payload) => {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload)
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `HTTP error! status: ${response.status}`);
  return body;
};

// rows: [{ line, values: { field: text } }] - the (possibly edited) rows of the file
// ignoredFields: columns the user switched off; the server leaves them out of the check and the import
export const validateRows = (rows, ignoredFields = []) => postJson('/api/import/preview', { rows, ignored_fields: ignoredFields });
export const commitImport = (rows, includeLines, ignoredFields = []) =>
  postJson('/api/import/commit', { rows, include_lines: includeLines, ignored_fields: ignoredFields });

// Uploads JPGs in small batches (the server caps request size). onProgress(done, total) is optional.
export const uploadImages = async (files, onProgress) => {
  const results = [];
  let batch = [];
  let batchBytes = 0;
  const flush = async () => {
    if (batch.length === 0) return;
    const form = new FormData();
    batch.forEach((f) => form.append('files', f));
    const response = await fetch('/api/images', { method: 'POST', body: form });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      batch.forEach((f) => results.push({ name: f.name, status: 'rejected', reason: body.error || `HTTP ${response.status}` }));
    } else {
      results.push(...body.results);
    }
    batch = [];
    batchBytes = 0;
    onProgress && onProgress(results.length, files.length);
  };
  for (const file of files) {
    if (batch.length >= 20 || batchBytes + file.size > 12 * 1024 * 1024) await flush();
    batch.push(file);
    batchBytes += file.size;
  }
  await flush();
  return results;
};

// ---- Possible duplicates / merge ----
export const fetchDuplicates = async (dismissed = false) => {
  const response = await fetch(`/api/duplicates${dismissed ? '?dismissed=1' : ''}`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
};

// Resolves to null when the Eircode no longer has duplicates (e.g. already merged)
export const fetchDuplicateGroup = async (eircode) => {
  const response = await fetch(`/api/duplicates/${encodeURIComponent(eircode)}`, { headers: { Accept: 'application/json' } });
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
};

export const setGroupDismissed = async (eircode, dismissed) => {
  const response = await fetch(`/api/duplicates/${encodeURIComponent(eircode)}/dismiss`, { method: dismissed ? 'POST' : 'DELETE' });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
};

// payload: { source_ids, keep_id, choices: { field: id }, image_from }
export const mergeListings = (payload) => postJson('/api/duplicates/merge', payload);

// ---- Price register ----
const priceRegisterCache = new Map();

// All price register entries for an Eircode, newest first. Cached per Eircode for the page's lifetime.
export const fetchPriceRegister = (eircode) => {
  if (!priceRegisterCache.has(eircode)) {
    const request = fetch(`/api/priceregister/${encodeURIComponent(eircode)}`, { headers: { Accept: 'application/json' } })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.json();
      })
      .then((body) => body.entries)
      .catch((err) => {
        priceRegisterCache.delete(eircode); // allow a retry the next time the popup opens
        throw err;
      });
    priceRegisterCache.set(eircode, request);
  }
  return priceRegisterCache.get(eircode);
};

// ---- Sales waiting for an Eircode ----
// params: { page, pageSize, q, since, suggested, sort, dir }
export const fetchPendingSales = async (params) => {
  const query = new URLSearchParams();
  query.set('page', params.page);
  query.set('page_size', params.pageSize);
  if (params.q) query.set('q', params.q);
  if (params.since) query.set('since', params.since);
  if (params.suggested) query.set('suggested', '1');
  query.set('sort', params.sort);
  query.set('dir', params.dir);
  const response = await fetch(`/api/pending?${query}`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.json();
};

// Accepts the Eircode for every pending sale at that address and moves those sales into the price register
export const setPendingEircode = (addressKey, eircode) => postJson('/api/pending/eircode', { address_key: addressKey, eircode });

// Same rule as the server: official Eircode routing-key letters, D6W as the one exception
export const EIRCODE_FORMAT = /^(?:[ACDEFHKNPRTVWXY][0-9]{2}|D6W)[0-9ACDEFHKNPRTVWXY]{4}$/;

// Finds the Eircode in pasted text such as "23 Taney Ave, Roebuck, Dublin 14, D14 AP80" (the last match wins).
// Returns it formatted with a space ("D14 AP80"), or null if the text has none.
export const extractEircode = (text) => {
  const matches = [...String(text || '').toUpperCase().matchAll(/\b((?:[ACDEFHKNPRTVWXY][0-9]{2}|D6W)) ?([0-9ACDEFHKNPRTVWXY]{4})\b/g)];
  if (matches.length === 0) return null;
  const last = matches[matches.length - 1];
  return `${last[1]} ${last[2]}`;
};
